import { useMemo } from 'react';
import {
  BulkFieldEditDialog,
  type BulkFieldSpec,
  type BulkResult,
} from '@/components/common/BulkFieldEditDialog';
import { workOrdersApi, type BulkWorkOrderChanges } from '@/api/services/work-orders.api';

interface Assignee {
  id: string;
  fullName?: string;
  email?: string;
}

/**
 * Fields a bulk edit may set on work orders.
 *
 * Status is not an ordinary field: the server runs it through the same
 * transition rules as a single order, so closing a batch will refuse the ones
 * that are not in a closable state and say which. That is why the reason field
 * sits beside it — it lands on the status history entry.
 */
const BASE_FIELDS: BulkFieldSpec[] = [
  {
    key: 'status',
    label: 'Status',
    type: 'select',
    options: [
      { value: 'DRAFT', label: 'Draft' },
      { value: 'PENDING_APPROVAL', label: 'Pending approval' },
      { value: 'APPROVED', label: 'Approved' },
      { value: 'SCHEDULED', label: 'Scheduled' },
      { value: 'IN_PROGRESS', label: 'In progress' },
      { value: 'ON_HOLD', label: 'On hold' },
      { value: 'COMPLETED', label: 'Completed' },
      { value: 'CANCELLED', label: 'Cancelled' },
      { value: 'CLOSED', label: 'Closed' },
    ],
    hint: 'Orders that cannot move to this status are listed afterwards, with the reason',
  },
  {
    key: 'statusReason',
    label: 'Reason for the status change',
    type: 'text',
    hint: 'Recorded on each order’s status history',
  },
  {
    key: 'priority',
    label: 'Priority',
    type: 'select',
    options: [
      { value: 'LOW', label: 'Low' },
      { value: 'MEDIUM', label: 'Medium' },
      { value: 'HIGH', label: 'High' },
      { value: 'URGENT', label: 'Urgent' },
      { value: 'EMERGENCY', label: 'Emergency' },
    ],
  },
  { key: 'scheduledStart', label: 'Scheduled start', type: 'date' },
  { key: 'scheduledEnd', label: 'Scheduled end', type: 'date' },
];

interface BulkWorkOrderEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workOrderIds: string[];
  assignees: Assignee[];
  onApplied: (result: BulkResult) => void;
}

export function BulkWorkOrderEditDialog({
  open,
  onOpenChange,
  workOrderIds,
  assignees,
  onApplied,
}: BulkWorkOrderEditDialogProps) {
  const fields = useMemo<BulkFieldSpec[]>(
    () => [
      ...BASE_FIELDS,
      {
        key: 'assignedToId',
        label: 'Assign to',
        type: 'select',
        options: assignees.map((a) => ({
          value: a.id,
          label: a.fullName || a.email || a.id,
        })),
        hint: 'Moves every selected order into this person’s queue',
      },
    ],
    [assignees]
  );

  return (
    <BulkFieldEditDialog
      open={open}
      onOpenChange={onOpenChange}
      ids={workOrderIds}
      fields={fields}
      noun="work order"
      submit={(ids, changes) =>
        workOrdersApi.bulkUpdate({
          workOrderIds: ids,
          changes: changes as BulkWorkOrderChanges,
        })
      }
      describeRow={(row) => ({
        title: (row.workOrderNumber as string) ?? row.id,
        subtitle: row.title as string | undefined,
      })}
      onApplied={onApplied}
    />
  );
}
